import type { FieldMarks } from "./fieldMarks";
/**
 * Framework-agnostic types describing the shape of a form document, its
 * versions, response sets and logic rules.
 *
 * Hoisted from apps/web/src/components/views/DocumentEditorView/types.ts —
 * any editor-specific types (palette groups, FontAwesome icons,
 * defaultData factories) stay in the host app.
 */

// ============================================================================
// Logic types
// ============================================================================

/** Condition operators for comparing element values */
export type ConditionOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "greater_than"
  | "less_than"
  | "greater_or_equal"
  | "less_or_equal"
  | "is_empty"
  | "is_not_empty"
  | "is_checked"
  | "is_not_checked";

/** Action types that can be performed on elements */
export type ActionType =
  | "show"
  | "hide"
  | "require"
  | "unrequire"
  | "enable"
  | "disable"
  | "set_value"
  | "calculate";

/** A leaf condition comparing an element's value */
export interface LogicCondition {
  kind: "condition";
  id: string;
  elementId: string;
  operator: ConditionOperator;
  value: unknown;
}

/** A group of conditions / nested groups combined with AND or OR */
export interface LogicGroup {
  kind: "group";
  id: string;
  operator: "AND" | "OR";
  children: LogicNode[];
}

/** Either a leaf condition or a nested group */
export type LogicNode = LogicCondition | LogicGroup;

/** A single action to perform on an element */
export type RuleAction = {
  id: string;
  type: ActionType;
  targetElementId: string;
  value?: unknown;
};

/** A complete logic rule */
export interface LogicRule {
  id: string;
  name: string;
  enabled: boolean;
  /** The root group; everything is a tree under here */
  root: LogicGroup;
  actions: RuleAction[];
}

/** Predicate for narrowing within recursive walks */
export const isLogicGroup = (node: LogicNode): node is LogicGroup =>
  node.kind === "group";

export const isLogicCondition = (node: LogicNode): node is LogicCondition =>
  node.kind === "condition";

// ============================================================================
// Document element types
// ============================================================================

export type DocumentElement = {
  id: string;
  /** Type identifier — see ELEMENT_TYPE in elementTypes.ts for the canonical set */
  typeId: string;
  name: string;
  data?: Record<string, unknown>;
  elements?: DocumentElement[];
  /** ISO timestamp when element was first published (undefined = never published) */
  publishedAt?: string;
  /** true = soft-deleted (hidden but retained for data integrity) */
  archived?: boolean;
  /**
   * Only used by children of a `columns` container — tracks which side
   * (left or right) the child belongs to. Absent on every other element
   * (and on top-level documents).
   */
  column?: "left" | "right";
};

// ============================================================================
// Response set types
// ============================================================================

/**
 * A single response option in a form-local response set (mirrors the global
 * DocumentResponse GraphQL type, but stored inside the document content JSON).
 */
export type LocalDocumentResponse = {
  name: string;
  /** Hex colour code, e.g. "#10B981" */
  colour: string;
  /** Optional numeric score; null = unscored */
  score: number | null;
  /**
   * When true, picking this response should be flagged for attention
   * (e.g. a Fail, At Risk, or Non-Compliant answer).
   */
  flagged: boolean;
};

/** One column on a form-local TABLE response set. */
export type LocalDocumentResponseSetColumn = {
  key: string;
  name: string;
  /**
   * When true, dropdown consumers render this column as an extra line
   * beneath the main option label.
   */
  showInDropdown?: boolean;
  /**
   * Controls whether the extra line is prefixed with the column name
   * ("Name: value") or rendered as the bare cell value. Defaults to
   * true for backwards-compat with sets saved before this flag existed.
   */
  showColumnNameInDropdown?: boolean;
};

/** One row on a form-local TABLE response set. */
export type LocalDocumentResponseSetRow = {
  id: string;
  values: Record<string, string>;
};

/**
 * A form-local response set, embedded in DocumentVersion.content. Used by
 * multi-choice + dropdown elements alongside global (per-client)
 * DocumentResponseSets. The id is form-local — distinct from numeric DB ids
 * of global sets.
 */
export type LocalDocumentResponseSet = {
  id: string;
  name: string;
  type: "MULTI_CHOICE" | "TABLE";
  /** When true, MultiChoice renders an "Other" pill + textbox for a custom response */
  allowOther: boolean;
  /**
   * When true AND the multi-choice element has allowMultiple, MultiChoice
   * renders a "None" pill that, when selected, clears all other selections.
   */
  allowNone: boolean;
  /**
   * When true, responses carry numeric scores that contribute to form
   * totals/reports. When false, scores are not meaningful (the editor
   * hides the score column).
   */
  isScoring: boolean;
  /** Populated for MULTI_CHOICE; empty/absent for TABLE. */
  responses: LocalDocumentResponse[];
  /** Populated for TABLE; absent for MULTI_CHOICE. */
  columns?: LocalDocumentResponseSetColumn[];
  /** Populated for TABLE; absent for MULTI_CHOICE. */
  tableRows?: LocalDocumentResponseSetRow[];
  /** TABLE+scoring: column key whose value supplies the per-row score. */
  scoreColumnKey?: string | null;
  /** TABLE+scoring: column key whose truthy value marks the row flagged. */
  flaggedColumnKey?: string | null;
  /** TABLE+scoring: manual max-score override. */
  maxScoreOverride?: number | null;
  /** TABLE: column whose row value displays as the dropdown's main label. */
  labelColumnKey?: string | null;
  /** TABLE: column whose row value persists as the filler's answer. */
  valueColumnKey?: string | null;
  /** TABLE: allow the filler to type a custom answer not in the rows. */
  allowUserEntry?: boolean;
  /** TABLE: when allowUserEntry, whether a custom answer is flagged. */
  userEntryFlagged?: boolean;
  /** TABLE: when allowUserEntry, score awarded for a custom answer. */
  userEntryScore?: number | null;
  /** TABLE: when allowUserEntry, fixed value to persist; null = typed text. */
  userEntryValue?: string | null;
};

// ============================================================================
// Document content + version types
// ============================================================================

/** The actual document content (elements and logic) */
export type EacoDocumentContent = {
  elements: DocumentElement[];
  logic?: LogicRule[];
  /** Form-local response sets (referenced by multi-choice elements) */
  responseSets?: LocalDocumentResponseSet[];
  /**
   * Document-level report settings (entity report documents only) —
   * versioned with the content so changes ride draft → publish.
   */
  settings?: ReportDocumentSettings;
  /**
   * Whether field titles mark required and optional questions. Unset
   * fields fall to the host's defaults (see fieldMarks.ts).
   */
  marks?: FieldMarks;
};

/** Document-level settings for entity report documents. */
export interface ReportDocumentSettings {
  /** Accent/tint colour (CSS colour) for section labels, dots, rules. */
  accentColor?: string | null;
  /** Repeat the owning company's name in the printed page header. */
  headerCompanyName?: boolean;
  /** Repeat the job name in the printed page header. */
  headerJobName?: boolean;
  /** "Page X of Y" in the printed page footer. */
  footerPageNumbers?: boolean;
  /** Render date in the printed page footer. */
  footerReportDate?: boolean;
}

/** Version status enum */
export type EacoDocumentVersionStatus = "draft" | "active" | "archived";

/** Individual version metadata with content */
export type EacoDocumentVersion = {
  /** Unique version ID (uuid) */
  id: string;
  /** Sequential version number (1, 2, 3...) */
  versionNumber: number;
  /** Version status: draft | active | archived */
  status: EacoDocumentVersionStatus;
  /** The actual document content */
  content: EacoDocumentContent;
  /** ISO timestamp when version was created */
  createdAt: string;
  /** ISO timestamp when version was published (only if status != draft) */
  publishedAt?: string;
  /** Optional version notes/changelog */
  notes?: string;
};

/** Main document type with versioning support */
export type EacoDocument = {
  /** Document ID */
  id: string;
  /** Document name */
  name: string;
  /** ID of current active version (undefined if never published) */
  activeVersionId?: string;
  /** ID of current draft (undefined if no unpublished changes) */
  draftVersionId?: string;
  /** All versions (draft, active, archived) */
  versions: EacoDocumentVersion[];
};

// Re-export LogicRule as EacoDocumentLogicRule for backwards compatibility
// with apps/web call sites that import the alias.
export type { LogicRule as EacoDocumentLogicRule };

// ============================================================================
// Output-binding payloads
//
// Form outputs are designer-authored documents that render a
// FormDocumentResult into a report-style view. The server-side
// `renderedOutput` resolver walks the output's active version content
// and bakes a "bound" payload into each output element's `data` field:
// - `form-results` → data.boundAnswers
// - `score`        → data.boundScore
// - `event-timeline` → data.boundEvents
// Renderers in @eaco/forms-ui-web / @eaco/forms-ui-native consume the
// bound payloads directly — no client-side GraphQL inside renderers.
// ============================================================================

/**
 * One row in a Form Results render — already label-resolved and
 * value-formatted by the server. `raw` keeps the original answer for
 * advanced renderers that want to format differently.
 */
export interface BoundAnswerPick {
  label: string;
  /** CSS colour from the response set, or null when the option has none. */
  colour: string | null;
}

export interface BoundAnswer {
  elementId: string;
  /** Human-readable label resolved from the form's version content. */
  label: string;
  /** The question's subtitle (the element's subLabel), when it has one. */
  subLabel?: string;
  /** Element typeId, so renderers can branch on shape if they need to. */
  typeId: string;
  /** Server-formatted display string. */
  formattedValue: string;
  /** Original answer envelope from FormDocumentResult.responses. */
  raw: unknown;
  /** Per-answer flagged flag (from scoring response sets). */
  flagged?: boolean;
  /** Per-answer score contribution (from scoring response sets). */
  score?: number | null;
  /**
   * The options a choice answer picked (multi-choice, dropdown), each with
   * the response's colour when its set defines one, so a results view can
   * show the answer the way the fill did. Absent for other element types.
   */
  picks?: BoundAnswerPick[];
  /**
   * Uploaded file references for image / signature / file answers, so a
   * renderer can show inline thumbnails instead of a text summary. `isImage`
   * is true for image-upload / signature (render as `<img>`); false for
   * generic file-upload (render as a name/link).
   */
  files?: { fileId: number; name: string | null }[];
  /** Whether `files` are images (image-upload / signature) vs generic files. */
  filesAreImages?: boolean;
  /**
   * Geo-tag answers: the point, the place ISM reverse-geocoded at submission
   * (null for results submitted before that existed), a static map image the
   * host supplied (PDFs) and a maps link.
   */
  geo?: BoundGeoAnswer;
}

/** The place stored inside a geo-tag value at submission (suburb / state / country). */
export interface GeoPlace {
  suburb: string | null;
  state: string | null;
  country: string | null;
  /** "Suburb, State, Country" — whatever parts resolved. */
  label: string;
}

export interface BoundGeoAnswer {
  lat: number;
  lng: number;
  place: GeoPlace | null;
  /** Static map image URL from the host (null when it has no map provider). */
  mapImageUrl: string | null;
  /** A link that opens the point in a maps app. */
  mapsHref: string;
}

// ============================================================================
// Hierarchical grouping types for FormResults
// ============================================================================

/**
 * Marker inserted into an answer list to signal the start of a structural
 * group (page, section, or heading). Renderers use these to inject headers
 * into a flat answer stream without nesting data structures.
 */
export interface AnswerGroupMarker {
  kind: 'page' | 'section' | 'heading';
  elementId: string;
  label: string;
  /** For headings: '1' | '2' | '3' | '4' */
  level?: string;
}

/**
 * Union of items that can appear in a hierarchically-grouped answer list.
 * Allows mixing actual answers with structural markers.
 */
export type AnswerListItem = BoundAnswer | AnswerGroupMarker;

/**
 * Type guard to distinguish structural markers from actual answers.
 */
export function isAnswerGroupMarker(item: AnswerListItem): item is AnswerGroupMarker {
  return 'kind' in item && ['page', 'section', 'heading'].includes((item as AnswerGroupMarker).kind);
}

/** Aggregate score block bound to the `score` element. */
export interface BoundScore {
  score: number | null;
  scoreMax: number | null;
  scorePercent: number | null;
  flaggedAnswerCount: number;
  /**
   * Score banding rules inherited from the parent FormDocument —
   * server populates this from `FormDocument.scoreThresholds` so the
   * Score renderer and the response-list badge share one palette.
   * Optional for backwards compatibility with rendered outputs that
   * predate the form-level setting.
   */
  thresholds?: Array<{
    from: number;
    to: number;
    colour: string;
    label?: string;
    /** Outcome icon key (see OUTCOME_ICONS) — drives the certificate variant. */
    icon?: string;
    /** Outcome description, e.g. "This certifies the mandatory safety check." */
    description?: string;
  }>;
}

/**
 * Bound payload for a `form-field` output element. Server emits one
 * per element after walking the result's metadata — the renderer
 * formats it according to the designer's style choice.
 */
export interface BoundFormField {
  /** Stable field id (see FORM_FIELDS in elementTypes.ts). */
  fieldId: string;
  /** Human-readable label (e.g. "Submitted"). */
  label: string;
  /** Already-formatted display value. */
  formattedValue: string;
}

/**
 * Bound payload for a `form-status` output element. Server derives
 * the current state from the result (submittedAt / future signedOffAt)
 * and emits both the stable id and a display label.
 */
export interface BoundFormStatus {
  /** Stable status id — see FORM_STATUSES in elementTypes.ts. */
  state: "in-progress" | "submitted" | "signed-off";
  /** Human-readable label, e.g. "Submitted". */
  label: string;
}

/**
 * Bound payload for a `form-signoff` output element — the full details
 * of a result's sign-off. The server populates it from the result's
 * sign-off record once a reviewer records a verdict (SATISFACTORY /
 * UNSATISFACTORY); `verdict` is null while the result is still awaiting
 * sign-off or sign-off is disabled, so the renderer shows an empty state.
 */
export interface BoundSignoff {
  /** Verdict once recorded; null while awaiting / not signed off. */
  verdict: "satisfactory" | "unsatisfactory" | null;
  /**
   * Display label for the verdict, using the form's configured accept/decline
   * labels (falls back to "Satisfactory" / "Unsatisfactory"). Null while
   * awaiting / not signed off.
   */
  label: string | null;
  /** ISO timestamp the verdict was recorded; null if not signed off. */
  signedOffAt: string | null;
  /** Reviewer display name; null when unresolved. */
  reviewerName: string | null;
  /**
   * The form's configured reviewer title (role / party the reviewer
   * represents), shown alongside the reviewer name. Null when unset.
   */
  reviewerTitle: string | null;
  /** Overall reviewer comment; null when none was left. */
  overallComment: string | null;
}

/** One row in an Event Timeline render. */
export interface BoundEvent {
  /** ISO-8601 timestamp. */
  at: string;
  /**
   * Stable event-type identifier (e.g. `result.created`,
   * `result.saved`, `result.submitted`, `result.answer-flagged`,
   * `result.signed-off-satisfactory`, `result.signed-off-unsatisfactory`).
   * Renderers can branch on this if they want per-type styling.
   */
  type: string;
  /** Short human-readable label (e.g. "Draft started", "Submitted"). */
  label: string;
  /** Display name of the acting principal — null for anonymous fillers. */
  actor?: string | null;
}

/**
 * A job's core fields as both report binders read them: the job report
 * (renderEntityReport) and form outputs filled against a job. One fetch
 * (apps/graphql fetchJobCore) produces it.
 */
export interface JobCoreData {
  domainId: string;
  oid: string;
  title: string | null;
  /** External reference / job number shown to users. */
  refId: string | null;
  /** Display status label (e.g. "Open", "Complete"). */
  status: string | null;
  /** The description as the viewer's role sees it, as plain text. */
  description: string | null;
  /**
   * The same description with its formatting (ISM's sanitised HTML, or
   * plain text with newlines from the older editors). Null when the
   * fetcher predates it; the binder then falls back to `description`.
   */
  descriptionHtml: string | null;
  /** "Opportunity description for suppliers": formatted, null when unset. */
  leadText: string | null;
  /** "Scope of works for suppliers": formatted, null when unset. */
  workOrderText: string | null;
  /** "Description visible to customer": formatted, null when unset. */
  descriptionForCustomer: string | null;
  addressFormatted: string | null;
  customerNames: string[];
  createdAt: string | null;
  dueAt: string | null;
  completedAt: string | null;
  /** Linked managed property's name (null when the job has none). */
  managedPropertyName: string | null;
  /** Site contact name off the job (null when unset). */
  siteContactName: string | null;
  /** Custom variable name → display value. */
  customVariables: Record<string, string>;
}

/**
 * The managed property a result was filled against (directly, or through
 * its job), as the output binder reads it. Display-ready strings.
 */
export interface ManagedPropertyCoreData {
  domainId: string;
  name: string | null;
  /** External reference (the property's code), e.g. "TKT". */
  refId: string | null;
  addressFormatted: string | null;
  customerName: string | null;
  contactName: string | null;
  /** Mobile number, else landline. */
  contactPhone: string | null;
  contactEmail: string | null;
  description: string | null;
}

/**
 * A calendar event the result was filled against, as the output binder
 * reads it. Display-ready except the instants, which stay ISO so the
 * formatter can apply the element's date format.
 */
export interface CalendarEventCoreData {
  domainId: string;
  name: string | null;
  /** Event type name (em_calendar_event_type), not the id. */
  typeName: string | null;
  /** UTC ISO; render in `timeZone`, which is the event's own zone. */
  startAtUtc: string | null;
  endAtUtc: string | null;
  /**
   * The event's IANA zone — its wall-clock truth, normally the site's.
   * Event times are rendered in THIS zone, not the filler's: an event
   * time means the time at the site. Null falls back to the binding zone.
   */
  timeZone: string | null;
  /** Minutes, as stored. */
  durationMinutes: number | null;
  addressFormatted: string | null;
  /** Attendee names, from em_calendar_event_entity. */
  assignedToNames: string[];
  notes: string | null;
  siteVisitRequired: boolean | null;
  finalised: boolean | null;
}

/**
 * What a result was filled against, loaded by the host (apps/graphql) from
 * the result's entity links. Each side is null when the result has no
 * such link or the viewer can't see it.
 */
export interface FormOutputContext {
  /** Same snapshot the job report binds (renderEntityReport's job core). */
  job: JobCoreData | null;
  managedProperty: ManagedPropertyCoreData | null;
  calendarEvent: CalendarEventCoreData | null;
}

/**
 * One "Linked Entity Data" field: a single value read off an entity the
 * result was filled against. Same shape for all three elements, and the
 * same shape as BoundJobField, so the app lowering can turn any of them
 * into a plain form-field.
 */
export interface BoundLinkedField {
  /** Stable field id within its entity's vocabulary. */
  fieldId: string;
  /** Default human-readable label (renderers apply data.labelOverride). */
  label: string;
  /** Already-formatted display value; '' when the entity has no value. */
  formattedValue: string;
  /**
   * Sanitised HTML of a formatted field (the job descriptions), carrying
   * the lists and line breaks `formattedValue` flattens. Absent for every
   * other field and for an empty value; renderers that cannot show HTML
   * (the app lowering, CSV-like outputs) keep reading `formattedValue`.
   */
  html?: string;
}

/** One row of a `details-table`. */
export interface BoundDetailsRow {
  /** `<source>.<fieldId>` (see DETAILS_FIELDS). */
  fieldId: string;
  label: string;
  /** Display value; '' when the source has no value. */
  value: string;
}

/** Bound payload for a `details-table` output element. */
export interface BoundDetailsTable {
  rows: BoundDetailsRow[];
}

/**
 * Per-response pick counts ISM's scorer stores on a result
 * (em_form_document_result.score_breakdown_json), one entry per scoring
 * response set the answered version uses. Counting at save keeps the
 * breakdown consistent with the stored score.
 */
export interface ScoreBreakdownSnapshot {
  v: 1;
  sets: ScoreBreakdownSetSnapshot[];
}

export interface ScoreBreakdownSetSnapshot {
  /** Response set id: numeric for global sets, `local_…` for form-local. */
  id: string;
  name: string;
  /** Every response (or TABLE row) in set order, zero counts included. */
  responses: Array<{
    label: string;
    colour: string | null;
    /** Null = non-scoring (N/A). */
    score: number | null;
    flagged: boolean;
    count: number;
  }>;
  /** Scoring questions on this set left unanswered. */
  unanswered: number;
}

export interface BoundScoreBreakdownRow {
  label: string;
  colour: string | null;
  count: number;
  /** The group's total (the denominator for this row). */
  total: number;
  /** Formatted per the element's display setting ("33 / 35", "33", "94%"). */
  display: string;
  /** A non-scoring (N/A) response. */
  notApplicable: boolean;
}

export interface BoundScoreBreakdownGroup {
  /** Set ids merged into this group (identical option lists merge). */
  setIds: string[];
  name: string;
  total: number;
  rows: BoundScoreBreakdownRow[];
}

/** Bound payload for a `score-breakdown` output element. */
export interface BoundScoreBreakdown {
  showSetName: boolean;
  groups: BoundScoreBreakdownGroup[];
}

/** Shape of `RenderedOutput.content` returned by the server resolver. */
export interface RenderedOutputContent extends EacoDocumentContent {
  /** Convenience top-level binding metadata for renderers that need it. */
  __binding?: {
    resultId: string;
    outputId: string;
    formName: string;
    /** IANA zone the instants were formatted in; renderers use it for the timeline and sign-off. */
    timeZone?: string | null;
  };
}

// ============================================================================
// Entity report bindings (job_report_document)
// ----------------------------------------------------------------------------
// Same contract as form-output bindings: the server (apps/graphql) walks
// a ReportDocument version's tree via renderEntityReport() and bakes a
// `bound*` payload into each report element's `data` field:
// - `job-attachments` → data.boundJobAttachments
// - `logo`            → data.boundLogo
// Renderers consume the bound payloads directly — no fetching inside
// renderers, so web preview, the print view, and forms-pdf-service all
// render identical documents from the same content JSON.
// ============================================================================

/**
 * Per-element item selection for selection-capable report elements
 * (job-attachments now; job-financials later). Templates default to
 * mode 'all'; the on-job customise wizard switches to 'manual' with an
 * explicit include-list of item ids (attachment ids, invoice ids, ...).
 */
export interface ElementSelection {
  mode: 'all' | 'manual';
  ids: string[];
}

/**
 * Ephemeral selection overrides keyed by element id — the wizard's
 * one-time-run payload, merged over element `data.selection` by the
 * binder before binding ("save to job" bakes the same shape into the
 * instance's version content instead).
 */
export type ReportSelections = Record<string, ElementSelection>;

/** One attachment tile in a `job-attachments` render. */
export interface BoundJobAttachmentItem {
  /** Attachment domain id — also the selection id (stringified). */
  fileId: number;
  name: string | null;
  /** Server-signed absolute URL (full-size); null when unavailable. */
  url: string | null;
  /** Server-signed absolute thumbnail URL; null when no thumbnail exists. */
  thumbnailUrl: string | null;
  /** True for image attachments (render as <img>); false → name/link tile. */
  isImage: boolean;
  caption: string | null;
  /** ISO timestamp the attachment was added; null when unknown. */
  createdAt: string | null;
}

/** Bound payload for a `job-attachments` report element. */
export interface BoundJobAttachments {
  items: BoundJobAttachmentItem[];
  /**
   * True when the binder dropped items — e.g. manual selection ids that
   * no longer exist on the job. Renderers may surface a note.
   */
  truncated: boolean;
}

/**
 * Bound payload for a `job-field` report element. The binder resolves
 * the field value (formatting dates per the element's format config)
 * so renderers never parse job data themselves.
 */
export interface BoundJobField {
  /** Stable field id (see JOB_FIELDS in elementTypes.ts). */
  fieldId: string;
  /** Default human-readable label (renderers apply data.labelOverride). */
  label: string;
  /** Already-formatted display value; '' when the job has no value. */
  formattedValue: string;
  /** Sanitised HTML of a formatted field; see BoundLinkedField.html. */
  html?: string;
}

/** One row in a `job-work-log` table. */
export interface BoundWorkLogRow {
  /** ISO start timestamp; null when unknown. */
  at: string | null;
  userName: string | null;
  notes: string | null;
  durationMinutes: number;
}

/** Bound payload for a `job-work-log` report element. */
export interface BoundWorkLog {
  rows: BoundWorkLogRow[];
  totalMinutes: number;
  /** e.g. "6h 45m". */
  totalFormatted: string;
}

/** One row in a `job-check-ins` table. */
export interface BoundCheckInRow {
  userName: string | null;
  /** ISO timestamps; checkedOutAt null while still on site. */
  checkedInAt: string | null;
  checkedOutAt: string | null;
  durationMinutes: number | null;
  isTransit: boolean;
}

/** Bound payload for a `job-check-ins` report element. */
export interface BoundCheckIns {
  rows: BoundCheckInRow[];
}

/** One row in a `job-calendar-events` table. */
export interface BoundCalendarEventRow {
  name: string | null;
  startAt: string | null;
  endAt: string | null;
  userNames: string[];
}

/** Bound payload for a `job-calendar-events` report element. */
export interface BoundCalendarEvents {
  rows: BoundCalendarEventRow[];
}

/** One row in a finance list (`job-invoices` / `job-bills` / …). */
export interface BoundFinanceLine {
  /** Graph OID of the document — the per-item selection key. */
  id: string;
  /** Document reference (invoice #, quote #, PO #, expense #). */
  ref: string | null;
  /** ISO-ish document date. */
  date: string | null;
  /** Counterparty (customer / vendor / supplier name). */
  party: string | null;
  /** Human-readable status label; null when not meaningful. */
  status: string | null;
  amount: number | null;
  amountFormatted: string;
}

/**
 * Bound payload shared by the six finance list elements (job-invoices,
 * job-bills, job-quotes-submitted, job-quotes-received,
 * job-purchase-orders, job-expenses) — they all bake
 * `data.boundFinanceList` and render through one component.
 */
export interface BoundFinanceList {
  rows: BoundFinanceLine[];
  totalFormatted: string;
}

/** One row in a `job-budget` table. */
export interface BoundBudgetRow {
  name: string;
  budgetedFormatted: string;
  actualFormatted: string;
}

/** Bound payload for a `job-budget` report element. */
export interface BoundBudget {
  rows: BoundBudgetRow[];
  totalBudgetedFormatted: string;
  totalActualFormatted: string;
}

/** One row in a `job-notes` table. */
export interface BoundJobNoteRow {
  /** Note OID — the per-item selection key. */
  id: string;
  /** ISO-ish created timestamp. */
  at: string | null;
  userName: string | null;
  /** Plain text; paragraphs separated by newlines. */
  note: string;
  flagged: boolean;
  /** True for replies — rendered indented under their parent. */
  isReply: boolean;
}

/** Bound payload for a `job-notes` report element. */
export interface BoundJobNotes {
  rows: BoundJobNoteRow[];
}

/** One row in a `job-action-items` table. */
export interface BoundActionItemRow {
  /** Action item OID — the per-item selection key. */
  id: string;
  name: string;
  typeName: string | null;
  assignedTo: string | null;
  /** ISO-ish due date; null when unset. */
  dueAt: string | null;
  isComplete: boolean;
  isPrivate: boolean;
}

/** Bound payload for a `job-action-items` report element. */
export interface BoundActionItems {
  rows: BoundActionItemRow[];
}

/** One row in a `job-tasks` table. */
export interface BoundJobTaskRow {
  name: string;
  mandatory: boolean;
  /** Human-readable state: Complete / In progress / Blocked / Open. */
  status: string;
  /** ISO timestamp of the most recent completion; null if never. */
  lastCompletedAt: string | null;
}

/** Bound payload for a `job-tasks` report element. */
export interface BoundJobTasks {
  rows: BoundJobTaskRow[];
}

/** One chip in a `job-tags` element. */
export interface BoundTagChip {
  name: string;
  /** CSS background (may be a gradient); null = neutral default. */
  background: string | null;
  color: string | null;
  border: string | null;
}

/** Bound payload for a `job-tags` report element. */
export interface BoundJobTags {
  chips: BoundTagChip[];
}

/** One message in a `job-chat-transcript` element. */
export interface BoundChatMessageRow {
  /** em_chat_message id — the per-item selection key. */
  id: string;
  /** ISO-ish posted timestamp. */
  at: string | null;
  userName: string | null;
  /** Plain-text rendering of the message. */
  message: string;
}

/** One cross-client chat on a job: the channel and its messages. */
export interface BoundExternalChatChannel {
  /** em_chat_channel id — the wizard groups message ticks under it. */
  channelId: string;
  name: string;
  /** The other company on the channel (null when it can't be resolved). */
  otherClientName: string | null;
  rows: BoundChatMessageRow[];
}

/**
 * Bound payload for a `job-chat-transcript` report element. `source`
 * mirrors the element's setting: INTERNAL binds `rows` (the job's internal
 * chat); EXTERNAL binds `groups` (one per cross-client chat) and leaves
 * `rows` empty.
 */
export interface BoundChatTranscript {
  source?: "internal" | "external";
  rows: BoundChatMessageRow[];
  groups?: BoundExternalChatChannel[];
}

/** Bound payload for a `logo` element in an entity report render. */
export interface BoundLogo {
  /** Server-signed absolute URL of the owner's logo; null when none. */
  url: string | null;
  ownerName: string | null;
}

/** Shape of `RenderedReport.content` returned by renderedJobReport. */
export interface RenderedReportContent extends EacoDocumentContent {
  /** The entity this render was bound against. */
  boundEntity?: {
    entityType: 'job';
    domainId: string;
    oid: string;
    name: string;
  };
  /** Header/footer strings resolved at bind time (settings consumers). */
  boundMeta?: {
    companyName: string | null;
    jobName: string | null;
  };
}

// ============================================================================
// Logic resolution types
// ============================================================================

export interface ResolvedElementState {
  hidden?: boolean;
  required?: boolean;
  disabled?: boolean;
  /** From `set_value` action — last writer wins, applied once when field is empty */
  setValue?: unknown;
  /** From `calculate` action — formula evaluation deferred; raw value passes through */
  calculated?: unknown;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Create a new DocumentElement from a type identifier.
 * `typeName` is the human-readable label (used as the element's initial name).
 */
export const createDocumentElement = (
  typeId: string,
  typeName: string,
  id: string,
): DocumentElement => ({
  id,
  name: typeName,
  typeId,
  data: {},
});

/** Helper to create a new empty condition */
export const createCondition = (id: string): LogicCondition => ({
  kind: "condition",
  id,
  elementId: "",
  operator: "equals",
  value: "",
});

/** Helper to create a new empty group (defaults to AND) */
export const createGroup = (
  id: string,
  operator: "AND" | "OR" = "AND",
): LogicGroup => ({
  kind: "group",
  id,
  operator,
  children: [],
});

/** Helper to create a new empty action */
export const createAction = (id: string): RuleAction => ({
  id,
  type: "show",
  targetElementId: "",
});

/**
 * Helper to create a new empty rule (root is an empty AND group).
 * Name defaults to '' — the RuleCard in the editor falls back to an
 * auto-generated condition summary when blank, so designers don't have
 * to invent a name for simple rules.
 */
export const createRule = (id: string, rootId: string): LogicRule => ({
  id,
  name: "",
  enabled: true,
  root: createGroup(rootId, "AND"),
  actions: [],
});

/** Prefix for palette draggable IDs to distinguish from existing document elements */
export const PALETTE_DRAGGABLE_PREFIX = "palette-";

// ── Per-form appearance & submission settings ────────────────────────────────
// Persisted as JSON objects on the FormDocument (see ISM FormDocument). Both
// are optional/partial — any missing field falls back to a platform default
// (see the theming model in ./theme). The web editor writes them; the fill
// renderer + confirmation screen consume them.

export type FormButtonCorners = "square" | "rounded" | "pill";
export type FormButtonFill = "solid" | "outline";
export type FormHeaderBackground = "tint" | "solid" | "plain";

export interface FormAppearance {
  /** Per-form accent colour (hex). Falls back to the platform accent. */
  accent?: string;
  button?: {
    corners?: FormButtonCorners;
    fill?: FormButtonFill;
  };
  header?: {
    showLogo?: boolean;
    background?: FormHeaderBackground;
  };
}

export type FormAfterSubmitMode = "stay" | "another" | "redirect";

export interface FormSubmission {
  /** Submit button label (default "Submit"). */
  submitLabel?: string;
  /**
   * Show the filler their live score while they fill (the Scoring
   * settings' "Show the filler their score"). Read by every fill surface
   * and the designer preview; stored here because these settings already
   * reach each of them.
   */
  showLiveScore?: boolean;
  confirmation?: {
    heading?: string;
    message?: string;
    /** Include a read-only recap of the filler's answers on the confirmation. */
    showSummary?: boolean;
  };
  afterSubmit?: {
    mode?: FormAfterSubmitMode;
    /** Only when mode = 'redirect'. */
    redirectUrl?: string;
    /** Seconds before redirect (default 2). */
    redirectDelaySec?: number;
  };
}
